import { GlobalHeaderProps, ProfileProps } from '@design-system/global-header';
import { NavigationDataItem } from '@design-system/global-header/dist/types/components/navigation/models';
import {
  LogoDataProps,
  SkipLinkProps,
  UtilityLinksDataItem,
} from '@design-system/global-header/dist/types/models';
import { navigate } from 'gatsby';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { CheckoutTypes, localStorageCartKey } from '~/context/shopify';
import { UseLanguageSwitchHook } from '~/hooks/useLanguageSwitch';
import { buttonMapper, buttonWithLinkMapper } from '~/mappers/button';
import { iconMapper } from '~/mappers/common';
import { linkBaseMapper, linkMapper } from '~/mappers/link';
import { LinkBase } from '~/mappers/link/models';
import { getStorageData } from '~/utils/storage';

import { imageMapper } from '../image';
import {
  LAST_SEARCHES_QUANTITY,
  LOCAL_STORAGE_LAST_SEARCHES_KEY,
} from './constants';
import {
  CartIconBlock,
  CartIconBlockProps,
  Header,
  Logo,
  MapHeaderItemsResult,
  NavigationLink,
  Profile,
  SearchBlock,
  SearchBlockProps,
  SkipLink,
} from './models';

const headerCartIconMapper = (
  cartIcon?: CartIconBlock,
  checkout?: CheckoutTypes
): CartIconBlockProps | undefined => {
  if (!cartIcon || !cartIcon?.[0] || !checkout) return undefined;

  const localStorageCheckout =
    (getStorageData(localStorageCartKey) as CheckoutTypes) || checkout;
  const { href, title, ariaLabel } = linkBaseMapper(cartIcon[0]);

  const actualCheckoutData = [localStorageCheckout, checkout].reduce((a, b) =>
    a?.updatedAt > b?.updatedAt ? a : b
  );
  const itemsQuantity = actualCheckoutData.lineItems?.reduce(
    (totalQuantity, lineItem) => totalQuantity + lineItem.quantity,
    0
  );

  return {
    ariaLabel,
    href: href as string, // TODO fix linkBaseMapper href undefined
    title,
    LinkComponent: TMPLinkWrap,
    itemsQuantity,
  };
};

const headerLogoMapper = ({
  smallImage,
  largeImage,
  link,
  alt,
}: Logo): LogoDataProps => ({
  imageSmall: imageMapper({ image: smallImage, alt }, true)!,
  imageLarge: imageMapper({ image: largeImage, alt }, true)!,
  ...{ link: { component: TMPLinkWrap, props: linkMapper(link) } },
});

const headerItemsMapper = (
  item: NavigationLink | LinkBase
): MapHeaderItemsResult => {
  const { href, title: label, ariaLabel } = linkBaseMapper(item);

  return {
    label,
    href: href as string, // TODO fix linkBaseMapper href undefined
    ariaLabel: ariaLabel || label,
    ...('submenuLabel' in item && { subMenuLabel: item.submenuLabel }),
    ...('submenu' in item &&
      item.submenu.length && { subItems: item.submenu.map(headerItemsMapper) }),
  };
};

const skipLinkMapper = ({ mode, link }: SkipLink): SkipLinkProps => ({
  ...linkMapper(link),
  mode,
});

const searchMapper = (search: SearchBlock[]): SearchBlockProps | undefined => {
  if (!search[0]) return undefined;

  const [
    {
      searchLabel,
      searchInputPlaceholder,
      ariaLabelOpen,
      ariaLabelClose,
      swipeLabel,
      lastSearchesTitle,
      searchButtonText,
      searchResultsLink,
    },
  ] = search;

  return {
    searchLabel,
    searchInputPlaceholder,
    ariaLabelOpen,
    ariaLabelClose,
    swipeLabel,
    localStorageLastSearchesKey: LOCAL_STORAGE_LAST_SEARCHES_KEY,
    lastSearchesQuantity: LAST_SEARCHES_QUANTITY,
    lastSearchesTitle,
    searchButtonText,
    customNavigate: (searchText) => {
      navigate(`${searchResultsLink[0].url}${searchText}`);
    },
    searchPageUrl: `${searchResultsLink[0].url}`,
  };
};

const languageSwitchMapper = (
  languageSwitch: ReturnType<UseLanguageSwitchHook>
) => {
  if (languageSwitch.list.length < 2) return undefined;

  const currLangSwitchSettings = languageSwitch.list.find(
    ({ currentPage }) => currentPage
  )!.languageSwitchSettings;

  return {
    title: currLangSwitchSettings.languageName,
    ariaLabel: currLangSwitchSettings.navButtonAria,
    languageData: languageSwitch.list
      .filter(({ currentPage }) => !currentPage)
      .map((item) => ({
        label: item.translated
          ? item.languageSwitchSettings.languageName
          : item.languageSwitchSettings.missingTranslation,
        href: item.url,
        locale: item.locale,
        translated: item.translated,
      })),
  };
};

const profileMapper = ({
  headline,
  greeting,
  icon,
  account,
  orders,
  savedProducts,
  register,
  logIn,
  logOut,
}: Profile) => ({
  headline,
  greeting,
  icon: iconMapper(icon[0]),
  account: linkMapper(account),
  orders: linkMapper(orders),
  savedProducts: linkMapper(savedProducts),
  register: buttonWithLinkMapper(register),
  logIn: buttonWithLinkMapper(logIn),
  logOut: buttonMapper(logOut),
  linkNavElement: TMPLinkWrap,
});

export const headerMapper = ({
  logo,
  burger: {
    openIcon: [burgerOpenIcon],
    closeIcon: [burgerCloseIcon],
    toggleLabel: burgerToggleLabel,
    closeLabel: burgerCloseLabel,
    openLabel: burgerOpenLabel,
  },
  navigation,
  moreButton: moreBtn,
  navigationAriaLabel,
  navigationCloseIcon: closeIcon,
  navigationOpenIcon: openIcon,
  utilityLinksName,
  utilityLinks,
  isHeaderSticky,
  mode,
  skipLink,
  languageSwitch,
  search,
  uid,
  cartIcon,
  profile,
  checkout,
}: Header): GlobalHeaderProps => ({
  mode,
  logo: headerLogoMapper(logo),
  burgerOpenIcon: iconMapper(burgerOpenIcon),
  burgerCloseIcon: iconMapper(burgerCloseIcon),
  burgerToggleLabel,
  burgerCloseLabel,
  burgerOpenLabel,
  navigationName: navigationAriaLabel,
  navigationData: navigation
    .map(headerItemsMapper)
    .filter((val) => Boolean(val)) as NavigationDataItem[],
  activeUrlPath:
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.hash.replace('/', '')}`
      : '',
  utilityLinksName,
  ...(utilityLinks.length && {
    utilityLinksData: utilityLinks
      .map(headerItemsMapper)
      .filter((val) => Boolean(val)) as UtilityLinksDataItem[],
  }),
  moreBtn,
  ...(openIcon?.length && { openIcon: iconMapper(openIcon[0]) }),
  ...(closeIcon?.length && { closeIcon: iconMapper(closeIcon[0]) }),
  isHeaderSticky,
  skipLink: skipLinkMapper(skipLink),
  linkNavElement: TMPLinkWrap,
  languageSwitch: languageSwitchMapper(languageSwitch),
  search: searchMapper(search),
  uid,
  cartIcon: headerCartIconMapper(cartIcon, checkout),
  profile: profile?.length
    ? (profileMapper(profile[0]) as unknown as ProfileProps)
    : undefined,
});
